import {
  addExportVisitor$,
  addImportVisitor$,
  type LexicalVisitor,
  type MdastHTMLNode,
  type MdastImportVisitor,
  realmPlugin,
} from '@mdxeditor/editor';
import {
  $createParagraphNode,
  $isElementNode,
  $isParagraphNode,
} from 'lexical';

import { isBlockAlignment } from '../textAlignment/utils';

/**
 * WHY THIS PLUGIN EXISTS
 *
 * Pressing Enter in MDXEditor creates empty ParagraphNodes (no children).
 * These are serialised as raw blank lines in the markdown output (e.g. `\n\n\n\n`).
 *
 * But the remark parser (used internally by MDXEditor on every setMarkdown /
 * initialise call) follows CommonMark: any number of consecutive blank lines
 * equals ONE paragraph break. Empty paragraphs are silently dropped.
 * Result: blank lines disappear when the page is reopened.
 *
 * The workaround of "adding a space" works because a paragraph with a space
 * character is not empty — it survives remark.
 *
 * Fix strategy: represent an empty paragraph as a standard markdown paragraph
 * containing a single U+00A0 NON-BREAKING SPACE character. NBSP is not a
 * whitespace-class character from remark's perspective, so it is preserved
 * through the round-trip. The downstream renderer (marked + html-react-parser)
 * renders ` ` as an invisible blank line — visually correct.
 *
 * On import, any paragraph whose sole content is a single NBSP is converted
 * back to an empty Lexical ParagraphNode (the marker is stripped).
 *
 * Scope: only truly empty, unaligned ParagraphNodes.
 * - Non-empty paragraphs → handled by default visitors unchanged.
 * - Aligned empty paragraphs → handled by textAlignmentPlugin.
 */

const EMPTY_PARAGRAPH_MARKER = ' ';

// ─── Types ────────────────────────────────────────────────────────────────────

type ExportVisitorParams = Parameters<
  NonNullable<LexicalVisitor['visitLexicalNode']>
>[0];

type MdastChild = ExportVisitorParams['mdastParent']['children'][number];

// ─── Export: empty ParagraphNode → paragraph( ) ─────────────────────────

const emptyParagraphExportVisitor: LexicalVisitor = {
  testLexicalNode: $isParagraphNode,
  // Priority 2: run before textAlignmentPlugin (priority 1) and default visitors.
  // Falls through with nextVisitor() for non-empty / aligned paragraphs so the
  // other visitors still handle those cases.
  priority: 2,
  visitLexicalNode({ lexicalNode, mdastParent, actions }) {
    if (!$isParagraphNode(lexicalNode)) {
      actions.nextVisitor();
      return;
    }

    if (lexicalNode.getChildren().length > 0) {
      actions.nextVisitor();
      return;
    }

    // Aligned empty paragraphs: textAlignmentPlugin handles those
    if (isBlockAlignment(lexicalNode.getFormatType())) {
      actions.nextVisitor();
      return;
    }

    // Append a standard markdown paragraph with a single NBSP.
    // appendToParent does NOT visit Lexical children — correct here since there
    // are no children and we supply the text content explicitly.
    actions.appendToParent(mdastParent, {
      type: 'paragraph',
      children: [{ type: 'text', value: EMPTY_PARAGRAPH_MARKER }],
    } as MdastChild);
  },
};

// ─── Import: paragraph( ) → empty ParagraphNode ─────────────────────────

// MdastHTMLNode is used as the generic type parameter solely for compatibility
// with MdastImportVisitor's constraint — at runtime this visitor only fires for
// standard 'paragraph' nodes (checked in testNode), never for JSX/HTML nodes.
const emptyParagraphImportVisitor: MdastImportVisitor<MdastHTMLNode> = {
  // Priority 2: run before the default paragraph visitor so NBSP-marker
  // paragraphs are intercepted before they produce a text node in the editor.
  priority: 2,
  testNode: (mdastNode) => {
    if (mdastNode.type !== 'paragraph') return false;

    const children = (
      mdastNode as unknown as {
        children?: Array<{ type: string; value?: string }>;
      }
    ).children;

    return (
      Array.isArray(children) &&
      children.length === 1 &&
      children[0].type === 'text' &&
      children[0].value === EMPTY_PARAGRAPH_MARKER
    );
  },
  visitNode({ lexicalParent, actions }) {
    if (!$isElementNode(lexicalParent)) {
      actions.nextVisitor();
      return;
    }

    // Append an empty paragraph — the NBSP marker is intentionally stripped
    lexicalParent.append($createParagraphNode());
  },
};

// ─── Plugin ───────────────────────────────────────────────────────────────────

export const emptyParagraphPlugin = realmPlugin({
  init(realm) {
    realm.pub(addExportVisitor$, emptyParagraphExportVisitor);
    realm.pub(addImportVisitor$, emptyParagraphImportVisitor);
  },
});
